import React, { useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, input, btn, btnGhost, SvgViewer, downloadBlob } from "./_drawingsCommon";

const SAMPLE_LINE = {
  id: "L-100-001-1A1",
  size_in: 6,
  operating_T_C: 250,
  operating_P_barg: 25,
  rating_class: 300,
  pipe_class: { id: "3A1" },
  material: "ASTM A106 Gr B",
  "3d_routing": {
    waypoints: [
      { x: 0, y: 0, z: 1.5 },
      { x: 25, y: 0, z: 1.5 },
      { x: 25, y: 8, z: 1.5 },
      { x: 25, y: 8, z: 6 },
    ],
  },
};

export default function IsometricPanel({ projectId }) {
  const [mode, setMode] = useState("sample"); // "sample" | "run" | "json"
  const [runId, setRunId] = useState("");
  const [lineId, setLineId] = useState("");
  const [lineJson, setLineJson] = useState(JSON.stringify(SAMPLE_LINE, null, 2));
  const [revision, setRevision] = useState("0");
  const [drawingNo, setDrawingNo] = useState("");
  const [blobUrl, setBlobUrl] = useState(null);
  const [filename, setFilename] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setErr(""); setBlobUrl(null); setBusy(true);
    try {
      let body = { revision, drawing_no: drawingNo || undefined };
      if (mode === "sample") body.line = SAMPLE_LINE;
      else if (mode === "run") { body.run_id = runId; body.line_id = lineId; }
      else if (mode === "json") body.line = JSON.parse(lineJson);
      const { data } = await api.post(`/projects/${projectId}/isometrics.svg`, body, { responseType: "blob" });
      const fn = `iso-${(body.line?.id || lineId || "drawing")}-${revision}.svg`;
      setFilename(fn);
      setBlobUrl(URL.createObjectURL(data));
    } catch (e) {
      // blob error → read as text
      try {
        const txt = await e?.response?.data?.text?.();
        setErr(formatErr(txt && JSON.parse(txt).detail) || formatErr(e?.response?.data?.detail) || String(e));
      } catch { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="iso-panel" style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 12 }}>
      <div style={card}>
        <div style={label}>Isometric Source</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
          {[["sample", "Sample line"], ["run", "From run"], ["json", "Paste JSON"]].map(([k, l]) => (
            <button key={k} data-testid={`iso-mode-${k}`}
              style={mode === k ? btn : btnGhost} onClick={() => setMode(k)}>{l}</button>
          ))}
        </div>

        {mode === "run" && (
          <>
            <input style={input} data-testid="iso-run-id" placeholder="run_id"
              value={runId} onChange={(e) => setRunId(e.target.value)} />
            <div style={{ height: 6 }} />
            <input style={input} data-testid="iso-line-id" placeholder="line_id"
              value={lineId} onChange={(e) => setLineId(e.target.value)} />
          </>
        )}
        {mode === "json" && (
          <textarea style={{ ...input, fontFamily: "monospace", minHeight: 220, fontSize: 11 }}
            data-testid="iso-json"
            value={lineJson} onChange={(e) => setLineJson(e.target.value)} />
        )}
        {mode === "sample" && (
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Built-in 6″ Class 3A1 sour-service line with 4 waypoints (90° elbow + vertical riser).
          </div>
        )}

        <div style={{ height: 10 }} />
        <div style={{ ...label, marginBottom: 4 }}>Drawing Meta</div>
        <input style={input} data-testid="iso-drawing-no" placeholder="Drawing No (optional)"
          value={drawingNo} onChange={(e) => setDrawingNo(e.target.value)} />
        <div style={{ height: 6 }} />
        <input style={input} data-testid="iso-revision" placeholder="Revision"
          value={revision} onChange={(e) => setRevision(e.target.value)} />

        <div style={{ height: 10 }} />
        <button style={{ ...btn, width: "100%" }} data-testid="iso-generate" onClick={generate} disabled={busy}>
          {busy ? "Generating…" : "Generate Isometric SVG"}
        </button>
        {blobUrl && (
          <button style={{ ...btnGhost, width: "100%", marginTop: 6 }}
            data-testid="iso-download" onClick={() => downloadBlob(blobUrl, filename)}>
            Download {filename}
          </button>
        )}
        {err && <div style={{ marginTop: 8, padding: 8, fontSize: 11, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4 }}>{err}</div>}
      </div>

      <SvgViewer blobUrl={blobUrl} testId="iso-svg" emptyText="No isometric generated yet — pick a source on the left and click Generate." />
    </div>
  );
}
